import { Badge } from '@/components/Atoms/Badge/Badge';
import { BadgeList } from '@/components/Atoms/BadgeList/BadgeList';
import { CardLinks } from '@/components/Atoms/CardLinks/CardLinks';
import { SkeletonPulse } from '@/components/Atoms/SkeletonPulse/SkeletonPulse';
import Updated from '@/components/Icons/Updated';
import { Card, CardActions, CardBody, CardMetadata } from '@/components/Molecules/Card/Card';
import { ContentPin } from '@/components/Molecules/ContentPin/ContentPin';
import { ContentTypeHeader } from '@/components/Molecules/ContentTypeHeader/ContentTypeHeader';
import { CountryUpdates } from '@/components/Molecules/CountryUpdates/CountryUpdates';
import { SaveButton } from '@/components/Molecules/SaveButton/SaveButton';
import { Stack } from '@/components/Molecules/Stack/Stack';
import { useFetchResponseHandler } from '@/hooks/useFetchResponseHandler';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement } from '@/interfaces';
import { ContentType } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import classNames from '@/utils/classNames';
import type { Country } from 'bnef';
import type { ElementType } from 'react';
import { useId } from 'react';
import type { FCl } from 'react-fc';

export interface ICountryCardProps extends IAnalyticsTraceableElement {
    headingLevel?: ElementType;
    country: Country;
    pinned?: boolean;
    minimal?: boolean;
}

type CountryOverviewResponse = {
    country: Country;
    updates: Record<string, number> | null;
};

export const CountryCard: FCl<ICountryCardProps> = ({
    analyticsBaseId,
    analyticsCategory = TrackingCategory.Countries,
    className,
    country,
    pinned,
    ...rest
}) => {
    const { t } = useTranslation();
    const titleId = useId();

    const { data, error } = useFetchResponseHandler<CountryOverviewResponse>(
        () => new BrowserHttpClient().get(`/countries/${country.id}/overview`),
        [country.id]
    );

    let links;
    if (data === undefined && !error) {
        links = <SkeletonPulse lines={3} />;
    } else if (data) {
        links = (
            <Stack>
                <CardLinks
                    links={[
                        { children: t('Country.Labels.overview'), href: country.url },
                        { children: t('Country.Labels.power_sector'), href: `${country.url}/power` },
                        { children: t('Country.Labels.technology_and_industry'), href: `${country.url}/technology` },
                    ]}
                />
                <CardMetadata
                    animate
                    items={[
                        data.country?.profile?.heroItem
                            ? {
                                  label: t('common:Labels.must_read'),
                                  value: (
                                      <a href={data.country.profile.heroItem.url}>
                                          {data.country.profile.heroItem.title}
                                      </a>
                                  ),
                              }
                            : undefined,
                        data.country && data.updates
                            ? {
                                  label: t('ContentBadge.updated'),
                                  value: <CountryUpdates country={data.country} updates={data.updates} />,
                              }
                            : undefined,
                    ]}
                />
            </Stack>
        );
    }

    return (
        <Card
            id={titleId}
            withImage={false}
            boxed
            headerImage={[
                `/api/images/country/${country.id}?size=600x-1`,
                `/api/images/country/${country.id}?size=1200x-1`,
            ]}
            href={country.url}
            className={classNames(className)}
            title={country.name}
            prefix={
                <BadgeList>
                    <ContentTypeHeader type={ContentType.countryprofile} />
                    {pinned && <ContentPin />}
                    {data?.updates && (
                        <Badge text={t('common:ContentBadge.updated')} colorVar="yellow" icon={<Updated />} />
                    )}
                </BadgeList>
            }
            data-testid="country-card"
            data-analytics-tracking={buildTrackingObject(TrackingAction.FollowLink, analyticsCategory, analyticsBaseId)}
            {...rest}
        >
            <CardBody>{links}</CardBody>
            <CardActions>
                <SaveButton
                    aria-describedby={titleId}
                    type="country"
                    id={country.id}
                    itemName={country.name}
                    analyticsBaseId={analyticsBaseId}
                />
            </CardActions>
        </Card>
    );
};
